import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../data/translations';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Users, Target, Eye, Award } from 'lucide-react';

const About = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const values = [
    {
      icon: Award,
      title: t('about.quality'),
      description: t('about.qualityText')
    },
    {
      icon: CheckCircle2,
      title: t('about.reliability'),
      description: t('about.reliabilityText')
    },
    {
      icon: Users,
      title: t('about.partnership'),
      description: t('about.partnershipText')
    },
    {
      icon: Target,
      title: t('about.integrity'),
      description: t('about.integrityText')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#1a3a52] to-[#2a4a62]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t('about.title')}
          </h1>
          <p className="text-xl text-gray-200 leading-relaxed">
            {t('about.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Company History */}
            <Card className="border-none shadow-2xl overflow-hidden">
              <div
                className="h-64 bg-cover bg-center"
                style={{
                  backgroundImage: `linear-gradient(rgba(26, 58, 82, 0.7), rgba(26, 58, 82, 0.7)), url('https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a')`
                }}
              />
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-[#c5a365] text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold">
                    2025
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a3a52]">
                    {language === 'en' ? 'Established in 2025' : 'تأسست في 2025'}
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {language === 'en' 
                    ? 'TAJ GULF TRADING L.L.C. is built upon a strong foundation of industry expertise. We are a proud sister concern of Avenue International LLC, a highly respected name in the restaurant industry since 2011.'
                    : 'تأسست شركة تاج الخليج للتجارة ذ.م.م على أساس قوي من الخبرة الصناعية. نحن شركة شقيقة فخورة لشركة Avenue International LLC، وهو اسم محترم للغاية في صناعة المطاعم منذ عام 2011.'}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en'
                    ? 'Avenue International LLC has cultivated excellence through trusted brands including Kesar Restaurant and Sweets and #968 The Food Studio.'
                    : 'قامت Avenue International LLC بتنمية التميز من خلال العلامات التجارية الموثوقة بما في ذلك مطعم وحلويات كيسار و#968 The Food Studio.'}
                </p>
              </CardContent>
            </Card>

            {/* Leadership Team */}
            <Card className="border-none shadow-2xl overflow-hidden">
              <div
                className="h-64 bg-cover bg-center"
                style={{
                  backgroundImage: `linear-gradient(rgba(26, 58, 82, 0.7), rgba(26, 58, 82, 0.7)), url('https://images.pexels.com/photos/7640434/pexels-photo-7640434.jpeg')`
                }}
              />
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Users className="h-10 w-10 text-[#c5a365]" />
                  <h3 className="text-2xl font-bold text-[#1a3a52]">
                    {language === 'en' ? 'Our Leadership' : 'قيادتنا'}
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-[#1a3a52]">
                      {language === 'en' ? 'Managing Director' : 'المدير العام'}
                    </p>
                    <p className="text-gray-600">Mr. Sanjay Asher</p>
                    <p className="text-sm text-gray-500">
                      {language === 'en' 
                        ? 'Seasoned professional with extensive HoReCa expertise'
                        : 'محترف متمرس مع خبرة واسعة في قطاع الفنادق والمطاعم'}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-[#1a3a52]">
                      {language === 'en' ? 'Directors' : 'المديرون'}
                    </p>
                    <p className="text-gray-600">Mr. Ashwin Sabnani • Mr. Dhinesh Sawlani</p>
                    <p className="text-sm text-gray-500">
                      {language === 'en'
                        ? 'Driving business growth and client trust since inception'
                        : 'قيادة نمو الأعمال وثقة العملاء منذ البداية'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <Card className="border-none shadow-xl bg-white hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-10">
                <div className="flex items-center space-x-3 mb-6">
                  <Target className="h-10 w-10 text-[#c5a365]" />
                  <h3 className="text-3xl font-bold text-[#1a3a52]">
                    {t('about.missionTitle')}
                  </h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {language === 'en'
                    ? 'To be the premier supplier of high-quality food and non-food products, uniforms, and packaging solutions, driving customer success and operational excellence within the HoReCa and related sectors across Oman.'
                    : 'أن نكون المورد الرئيسي للمنتجات الغذائية وغير الغذائية عالية الجودة، والزي الرسمي، وحلول التعبئة والتغليف، مما يدفع نجاح العملاء والتميز التشغيلي في قطاع الفنادق والمطاعم والقطاعات ذات الصلة في جميع أنحاء عمان.'}
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="border-none shadow-xl bg-white hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-10">
                <div className="flex items-center space-x-3 mb-6">
                  <Eye className="h-10 w-10 text-[#c5a365]" />
                  <h3 className="text-3xl font-bold text-[#1a3a52]">
                    {t('about.visionTitle')}
                  </h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {language === 'en'
                    ? 'To continuously innovate and expand our offerings, building enduring partnerships through unwavering commitment to quality, reliability, and exceptional customer satisfaction.'
                    : 'الابتكار المستمر وتوسيع عروضنا، وبناء شراكات دائمة من خلال الالتزام الثابت بالجودة والموثوقية ورضا العملاء الاستثنائي.'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a3a52] mb-4">
              {t('about.valuesTitle')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-[#c5a365]/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Award className="h-10 w-10 text-[#c5a365]" />
                </div>
                <h3 className="text-xl font-bold text-[#1a3a52] mb-3">
                  {language === 'en' ? 'Quality Excellence' : 'التميز في الجودة'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en' 
                    ? 'Commitment to excellence in every product and service'
                    : 'الالتزام بالتميز في كل منتج وخدمة'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-[#c5a365]/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-10 w-10 text-[#c5a365]" />
                </div>
                <h3 className="text-xl font-bold text-[#1a3a52] mb-3">
                  {language === 'en' ? 'Customer Priority' : 'أولوية العملاء'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en'
                    ? 'Customers as our highest priority, exceeding expectations'
                    : 'العملاء كأولويتنا القصوى، تجاوز التوقعات'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-[#c5a365]/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Users className="h-10 w-10 text-[#c5a365]" />
                </div>
                <h3 className="text-xl font-bold text-[#1a3a52] mb-3">
                  {language === 'en' ? 'Employee Value' : 'قيمة الموظفين'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en'
                    ? 'Employees as our greatest asset and foundation'
                    : 'الموظفون كأعظم أصولنا وأساسنا'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-[#c5a365]/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Target className="h-10 w-10 text-[#c5a365]" />
                </div>
                <h3 className="text-xl font-bold text-[#1a3a52] mb-3">
                  {language === 'en' ? 'Integrity' : 'النزاهة'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en'
                    ? 'Highest ethical standards in all business practices'
                    : 'أعلى المعايير الأخلاقية في جميع الممارسات التجارية'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-16 bg-gradient-to-r from-[#1a3a52] to-[#2a4a62]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-5xl font-bold text-[#c5a365] mb-2">2025</p>
              <p className="text-white text-lg">{language === 'en' ? 'Established' : 'تأسست'}</p>
            </div>
            <div>
              <p className="text-5xl font-bold text-[#c5a365] mb-2">200+</p>
              <p className="text-white text-lg">{t('home.customersLabel')}</p>
            </div>
            <div>
              <p className="text-5xl font-bold text-[#c5a365] mb-2">1000+</p>
              <p className="text-white text-lg">{t('home.productsLabel')}</p>
            </div>
            <div>
              <p className="text-5xl font-bold text-[#c5a365] mb-2">95%+</p>
              <p className="text-white text-lg">{language === 'en' ? 'Customer Satisfaction' : 'رضا العملاء'}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
